import PropTypes from 'prop-types';
import React, {useCallback, useContext, useEffect, useState} from 'react';

import {RESPONSE_STATUSES} from '@/src/app/constants/api';
import useCourseDetailsActions from '@/src/app/store/courseDetails/useCourseDetailsActions';
import useReviewsWidget from '@/src/app/store/reviewsWidget/useReviewsWidget';
import useLoading from '@/src/hooks/useLoading';
import TextLocal from '@/src/textLocal/containers/TextLocal';

import PopupContext from '../../popup/utils/PopupContext';
import {Button, LoadingWrapper, Separator, Text} from '../../ui';
import ReviewsListLayout from '../components/ReviewsListLayout';
import WriteReviewPopup from '../components/WriteReviewPopup';
import css from '../styles/Review.module.css';
import getCourseReviews from '../utils/getReviews';
import sendNewReview from '../utils/sendNewReview';

const REVIEWS_PER_PAGE = 3;

const ReviewsList = ({
  isWritingReviewAllowed,
  courseId,
  toggleLeaveReviewAllowed,
}) => {
  if (!courseId) return null;

  const {reviews, addCourseReview, setCourseReviews} = useReviewsWidget();
  const {updateCourseRating} = useCourseDetailsActions();
  const {openPopup} = useContext(PopupContext);
  const {isLoading, setLoading} = useLoading();

  const [offset, setOffset] = useState(REVIEWS_PER_PAGE);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    const {status, responseBody} = await getCourseReviews(courseId);

    if (status === RESPONSE_STATUSES.SUCCESS) {
      setCourseReviews(responseBody);
    }

    setLoading(false);
  }, [offset]);

  useEffect(() => {
    fetchReviews();
  }, []);

  const handleAddNewReviewClick = useCallback(
    async (values) => {
      const {status, responseBody} = await sendNewReview(
        courseId,
        values.rating,
        values.comment
      );

      if (status === RESPONSE_STATUSES.SUCCESS) {
        updateCourseRating(values.rating);
        toggleLeaveReviewAllowed && toggleLeaveReviewAllowed();

        addCourseReview(responseBody);
      }
    },
    [courseId, reviews]
  );

  const handleLeaveReviewClick = useCallback(() => {
    // @ts-ignore
    openPopup({
      popup: <WriteReviewPopup onAddNewReviewClick={handleAddNewReviewClick} />,
      options: {withCloseButton: true},
    });
  }, [handleAddNewReviewClick]);

  const onShowMoreClick = useCallback(() => {
    setOffset(offset + REVIEWS_PER_PAGE);
  }, [offset]);

  const filteredReviews = reviews.data.filter(
    (review, index) => index <= offset
  );

  return (
    <div>
      <>
        <div className={css.reviewsHeader}>
          <div className={css.reviewsTitle}>
            <Text
              fontSize={Text.FONT_SIZE.SIZE_36}
              fontFamily={Text.FONT_FAMILY.RUBIK}
              lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
              tagComponent={Text.TAG.SPAN}
              fontWeight={Text.FONT_WEIGHT.MEDIUM}
            >
              <TextLocal id={'courseDetails.reviews'} />
            </Text>
          </div>
          {isWritingReviewAllowed && (
            <Button
              height={Button.HEIGHT.SMALL}
              onClick={handleLeaveReviewClick}
            >
              <Text
                fontSize={Text.FONT_SIZE.SIZE_14}
                fontWeight={Text.FONT_WEIGHT.MEDIUM}
                lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1}
              >
                <TextLocal id={'courseDetails.leave-review'} />
              </Text>
            </Button>
          )}
        </div>

        <div className={css.reviewsWrapper}>
          <LoadingWrapper isLoading={isLoading} hideContentBeforeLoad>
            <ReviewsListLayout reviews={filteredReviews} />
          </LoadingWrapper>
        </div>

        {filteredReviews.length < reviews.data.length && (
          <>
            <Separator borderWeight={Separator.BORDER_WEIGHT.THIN} />
            <div className={css.reviewsButtonWrapper}>
              <Button
                onClick={onShowMoreClick}
                color={Button.COLOR.SECONDARY}
                height={Button.HEIGHT.SMALL}
                border={Button.BORDER.BOLD}
              >
                <TextLocal id={'courseDetails.see-more'} />
              </Button>
            </div>
          </>
        )}
      </>
    </div>
  );
};

ReviewsList.propTypes = {
  isWritingReviewAllowed: PropTypes.bool.isRequired,
  toggleLeaveReviewAllowed: PropTypes.func,
  courseId: PropTypes.number,
};

export default ReviewsList;
